import * as i0 from '@angular/core';
import { inject, Directive, Input, HostListener, Component, ViewEncapsulation, ChangeDetectionStrategy, ContentChild, Injector, Injectable, TemplateRef, EventEmitter, Output, NgModule } from '@angular/core';
import * as i1 from '@odx/angular';
import { Controller, detectControllerChanges, CoreModule } from '@odx/angular';
import { DynamicViewDirective, DynamicViewService } from '@odx/angular/cdk/dynamic-view';
import { AreaHeaderSize, AreaHeaderComponent, AreaHeaderModule } from '@odx/angular/components/area-header';
import { ButtonComponent } from '@odx/angular/components/button';
import { deepmerge, CSSComponent, CSSModifier } from '@odx/angular/internal';
import { Subject, share, finalize } from 'rxjs';
import { __decorate, __metadata } from 'tslib';
import { injectElement, hasChanged, isTemplateRef, createConfigTokens, Queue, getUniqueId, waitForAnimations, deferFn } from '@odx/angular/utils';
import { ActionGroupComponent } from '@odx/angular/components/action-group';
import { IconComponent, IconSet } from '@odx/angular/components/icon';
import { A11yModule } from '@odx/angular/cdk/a11y';
import * as i1$1 from '@angular/common';
import { ButtonGroupComponent } from '@odx/angular/components/button-group';

const ModalHeroVariant = {
    PRIMARY: 'primary',
    SUCCESS: 'success',
    CONFIRMATION: 'confirmation',
    DANGER: 'danger',
};

/**
 * A reference to a dynamically created modal, providing methods to manipulate, close, or update the modal's state
 * and options.
 * This class extends the `Controller` base class from `@odx/angular`, adding modal-specific functionalities like
 * handling closures and dismissals.
 *
 * @template Data - The type of data passed to the modal.
 * @template Result - The type of the result emitted when the modal is closed.
 *
 * @see {Controller}
 */
class ModalRef extends Controller {
    /** Gets the unique identifier for the modal instance.
     *
     * @returns {string} The unique identifier for the modal instance.
     */
    get id() {
        return this.options.id;
    }
    /** Gets the ARIA label ID for the modal title, based on the modal's ID.
     *
     * @returns {string} The ARIA label ID for the modal title.
     */
    get modalTitleId() {
        return `${this.options.id}-title`;
    }
    /** Gets the data passed to the modal. This is useful for modal initialization.
     *
     * @returns {Data} The data passed to the modal.
     */
    get data() {
        return this.internalOptions.data;
    }
    /** Gets the current options for the modal. These options include configurations.
     *
     * @template Data - The type of data passed to the modal.
     * @returns {ModalOptions<Data>} The current options for the modal.
     */
    get options() {
        return this.internalOptions;
    }
    constructor(internalOptions, isActive) {
        super();
        this.internalOptions = internalOptions;
        this.onClose$$ = new Subject();
        this.onDismiss$$ = new Subject();
        /** An observable that emits when the modal is closed with a result. */
        this.onClose$ = this.onClose$$.pipe(share({ resetOnRefCountZero: true }));
        /** An observable that emits when the modal is dismissed without a result. */
        this.onDismiss$ = this.onDismiss$$.pipe(share({ resetOnRefCountZero: true }));
        /** An observable that merges the onClose$ and onDismiss$ streams for cases when any kind of teardown is needed. */
        this.onDestroy$ = deepmerge(this.onClose$, this.onDismiss$);
        this.isActive = () => isActive(this);
    }
    /**
     * Updates the modal's options.
     *
     * @param {Partial<ModalOptions<Data>>} options A partial set of options to update.
     */
    update(options) {
        this.internalOptions = deepmerge(this.internalOptions, options);
        this.triggerControllerChange();
    }
    /**
     * Dismisses the modal. This does not result in an output value.
     *
     * @param {boolean} [force=false] Whether to force dismissal regardless of the `canDismiss` condition.
     */
    dismiss(force = false) {
        if (!force && !this.isActive())
            return;
        if (this.options.canDismiss?.(this.data) === false)
            return;
        this.onDismiss$$.next();
        this.destroy();
    }
    /**
     * Closes the modal with a result.
     *
     * @param {Result} result The result to emit when the modal closes.
     */
    close(result) {
        if (!this.isActive())
            return;
        if (this.options.canClose?.(this.data) === false)
            return;
        this.onClose$$.next(result);
        this.destroy();
    }
    /** Completes the onClose and onDismiss streams and cleans up resources. */
    destroy() {
        this.onClose$$.complete();
        this.onDismiss$$.complete();
    }
}

const ModalSize = {
    XSMALL: 'xsmall',
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
};

const ModalVariant = {
    DEFAULT: 'default',
    SIDESHEET: 'sidesheet',
};

/**
 * Utility function to inject a reference to a modal. This function simplifies the injection process by typing the `ModalRef` to the specific data and result expected by the consumer.
 *
 * This approach abstracts away Angular's inject function, providing direct access to the modal reference with the correct type. It is commonly used in components or services that interact with a modal dialog to manipulate its data, close it, or perform other related actions.
 *
 * @template Data The type of data passed to the modal.
 * @template Result The type of result expected when the modal closes.
 * @returns {ModalRef<Data, Result>} A strongly typed reference to the modal.
 *
 * @example
 * ```ts
 * // Injecting a modal reference in a component or service:
 * const modalRef = injectModalRef<SomeDataType, SomeResultType>();
 *
 * // Using the modal reference to close the modal and return a result:
 * modalRef.close(someResult);
 *
 * // Accessing the data provided to the modal:
 * const modalData = modalRef.data;
 * ```
 */
function injectModalRef() {
    return inject((ModalRef));
}

/**
 * Provides a configuration for the Angular dependency injection system to inject a specific instance of `ModalRef`.
 * This function is particularly useful when you want to configure a provider for a `ModalRef` within an Angular module or component.
 *
 * @template D The data type expected by the modal.
 * @template R The result type that the modal will produce.
 * @param {ModalRef<D, R>} modalRef An instance of `ModalRef` to be provided.
 * @returns {ValueProvider} An Angular value provider that can be used in module or component providers arrays.
 */
function provideModalRef(modalRef) {
    return { provide: ModalRef, useValue: modalRef };
}

/**
 * A directive that enables an element to close or dismiss a modal when clicked.
 * It can be used on any element within a modal to trigger the close or dismiss action, optionally passing a result back to the modal opener.
 *
 * The directive accepts an input value that determines the result to be passed back on modal close. If no value is provided, or it's explicitly set to `null`,
 * the modal is dismissed without sending a result. If the input value is `undefined`, the modal is dismissed via the `dismiss` method without passing any result.
 *
 * @template T - The type of the result to be emitted when the modal closes. Defaults to `unknown`.
 *
 * @example
 * Closing a modal and passing a result:
 * ```html
 * <button [odxModalClose]="myResult">Close and send result</button>
 * ```
 *
 * Simply dismissing a modal without passing a result:
 * ```html
 * <button odxModalClose>Dismiss</button>
 * ```
 */
class ModalCloseDirective {
    constructor() {
        this.modalRef = injectModalRef();
        /**
         * Represents the result to be passed back to the modal opener when the modal is closed.
         * @template T - The type of the result returned.
         */
        this.result = null;
    }
    onClick() {
        const result = this.result === '' ? null : this.result;
        if (result === undefined) {
            this.modalRef.dismiss();
        }
        else {
            this.modalRef.close(result ?? null);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalCloseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ModalCloseDirective, isStandalone: true, selector: "[odxModalClose]", inputs: { result: ["odxModalClose", "result"] }, host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[odxModalClose]',
                }]
        }], propDecorators: { result: [{
                type: Input,
                args: ['odxModalClose']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * Represents a directive for the content of a modal.
 */
let ModalContentDirective = class ModalContentDirective {
    constructor() {
        this.element = injectElement();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ModalContentDirective, isStandalone: true, selector: "odx-modal-content", ngImport: i0 }); }
};
ModalContentDirective = __decorate([
    CSSComponent('modal__content')
], ModalContentDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalContentDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: 'odx-modal-content',
                }]
        }] });

/**
 * A directive that enables an element to dismiss a modal when clicked. This can be applied
 * to any clickable element within a modal, such as buttons or links, to provide a declarative
 * approach to dismissing the modal.
 *
 * Utilizes `injectModalRef` to access the modal reference associated with the containing modal,
 * enabling the directive to call the `dismiss` method on the modal reference upon the click event.
 *
 * @example
 * Dismissing a modal using the odxModalDismiss directive on a button:
 * ```html
 * <button odxModalDismiss>Cancel</button>
 * ```
 *
 * This directive simplifies modal dismissal by removing the need for manually wiring click events
 * to dismiss methods in the component class, thereby enhancing template readability and maintainability.
 */
class ModalDismissDirective {
    constructor() {
        this.modalRef = injectModalRef();
    }
    onClick() {
        this.modalRef.dismiss();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalDismissDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ModalDismissDirective, isStandalone: true, selector: "[odxModalDismiss]", host: { listeners: { "click": "onClick()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalDismissDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[odxModalDismiss]',
                }]
        }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

/**
 * Directive that prevents the default form method behavior on submit buttons inside an HTML Dialog form.
 */
class PreventFormMethodDialogDirective {
    onClick(event) {
        event.preventDefault();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PreventFormMethodDialogDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: PreventFormMethodDialogDirective, isStandalone: true, selector: "[formmethod=dialog]", host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PreventFormMethodDialogDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: '[formmethod=dialog]',
                }]
        }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

/**
 * Directive that prevents the default form submission behavior inside an HTML Dialog form.
 */
class PreventMethodDialogDirective {
    onFormSubmit(event) {
        event.preventDefault();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PreventMethodDialogDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: PreventMethodDialogDirective, isStandalone: true, selector: "form[method=dialog]", host: { listeners: { "submit": "onFormSubmit($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PreventMethodDialogDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: 'form[method=dialog]',
                }]
        }], propDecorators: { onFormSubmit: [{
                type: HostListener,
                args: ['submit', ['$event']]
            }] } });

/**
 * Represents the footer component of a modal.
 */
let ModalFooterComponent = class ModalFooterComponent {
    constructor() {
        this.element = injectElement();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: ModalFooterComponent, isStandalone: true, selector: "odx-modal-footer", ngImport: i0, template: "<ng-content />\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
ModalFooterComponent = __decorate([
    CSSComponent('modal__footer')
], ModalFooterComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalFooterComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-modal-footer', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content />\n" }]
        }] });

/**
 * Represents the header component of a modal.
 */
let ModalHeaderComponent = class ModalHeaderComponent {
    constructor() {
        this.modalRef = injectModalRef();
        this.element = injectElement();
        detectControllerChanges(this.modalRef).subscribe();
    }
    ngAfterContentInit() {
        if (this.areaHeaderComponent) {
            this.areaHeaderComponent.size = AreaHeaderSize.MEDIUM;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: ModalHeaderComponent, isStandalone: true, selector: "odx-modal-header", queries: [{ propertyName: "areaHeaderComponent", first: true, predicate: AreaHeaderComponent, descendants: true }], ngImport: i0, template: "<ng-content select=\"odx-area-header\" />\n@if (modalRef.options.dismissable) {\n  <odx-action-group class=\"odx-modal__dismiss\">\n    <button odxButton odxModalDismiss><odx-icon name=\"close\" iconSet=\"core\" /></button>\n  </odx-action-group>\n}\n", dependencies: [{ kind: "ngmodule", type: CoreModule }, { kind: "component", type: ActionGroupComponent, selector: "odx-action-group", inputs: ["reverse"] }, { kind: "component", type: ButtonComponent, selector: "button[odxButton], a[odxButton]", inputs: ["variant", "size"] }, { kind: "component", type: IconComponent, selector: "odx-icon", inputs: ["inline", "size", "name", "iconSet", "identifier"] }, { kind: "directive", type: ModalDismissDirective, selector: "[odxModalDismiss]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
ModalHeaderComponent = __decorate([
    CSSComponent('modal__header'),
    __metadata("design:paramtypes", [])
], ModalHeaderComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalHeaderComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-modal-header', imports: [CoreModule, ActionGroupComponent, ButtonComponent, IconComponent, ModalDismissDirective], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content select=\"odx-area-header\" />\n@if (modalRef.options.dismissable) {\n  <odx-action-group class=\"odx-modal__dismiss\">\n    <button odxButton odxModalDismiss><odx-icon name=\"close\" iconSet=\"core\" /></button>\n  </odx-action-group>\n}\n" }]
        }], ctorParameters: () => [], propDecorators: { areaHeaderComponent: [{
                type: ContentChild,
                args: [AreaHeaderComponent]
            }] } });

/**
 * Represents a modal hero component.
 */
let ModalHeroComponent = class ModalHeroComponent {
    constructor() {
        this.element = injectElement();
        /**
         * The modal reference of the modal hero component.
         *
         * @type {ModalRef}
         */
        this.modalRef = injectModalRef();
        /**
         * The icon set of the modal hero component.
         *
         * @type {IconSet}
         * @default IconSet.CORE
         */
        this.iconSet = IconSet.CORE;
        detectControllerChanges(this.modalRef).subscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalHeroComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: ModalHeroComponent, isStandalone: true, selector: "odx-modal-hero", inputs: { icon: "icon", iconSet: "iconSet", variant: "variant" }, ngImport: i0, template: "@if (icon) {\n  <odx-icon class=\"odx-modal-hero__icon\" [name]=\"icon\" [iconSet]=\"iconSet\" size=\"xlarge\" />\n}\n\n<div class=\"odx-modal-hero__title odx-heading-4\">\n  <ng-content />\n</div>\n@if (modalRef.options.dismissable) {\n  <odx-action-group class=\"odx-modal-hero__dismiss\">\n    <button odxButton odxModalDismiss>\n      <odx-icon name=\"close\" iconSet=\"core\" />\n    </button>\n  </odx-action-group>\n}\n", dependencies: [{ kind: "ngmodule", type: CoreModule }, { kind: "component", type: ActionGroupComponent, selector: "odx-action-group", inputs: ["reverse"] }, { kind: "component", type: ButtonComponent, selector: "button[odxButton], a[odxButton]", inputs: ["variant", "size"] }, { kind: "component", type: IconComponent, selector: "odx-icon", inputs: ["inline", "size", "name", "iconSet", "identifier"] }, { kind: "directive", type: ModalDismissDirective, selector: "[odxModalDismiss]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], ModalHeroComponent.prototype, "variant", void 0);
ModalHeroComponent = __decorate([
    CSSComponent('modal-hero'),
    __metadata("design:paramtypes", [])
], ModalHeroComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalHeroComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-modal-hero', standalone: true, imports: [CoreModule, ActionGroupComponent, ButtonComponent, IconComponent, ModalDismissDirective], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "@if (icon) {\n  <odx-icon class=\"odx-modal-hero__icon\" [name]=\"icon\" [iconSet]=\"iconSet\" size=\"xlarge\" />\n}\n\n<div class=\"odx-modal-hero__title odx-heading-4\">\n  <ng-content />\n</div>\n@if (modalRef.options.dismissable) {\n  <odx-action-group class=\"odx-modal-hero__dismiss\">\n    <button odxButton odxModalDismiss>\n      <odx-icon name=\"close\" iconSet=\"core\" />\n    </button>\n  </odx-action-group>\n}\n" }]
        }], ctorParameters: () => [], propDecorators: { icon: [{
                type: Input
            }], iconSet: [{
                type: Input
            }], variant: [{
                type: Input
            }] } });

/**
 * Represents a basic modal component that can be used to display dynamic content, handle modal actions, and interact
 * with other components through dependency injection. It includes common modal features such as headers, footers, and dynamic content areas.
 * This component uses the `ModalRef` to manage its state and interactions.
 *
 * The `BasicModalComponent` is designed to be flexible and reusable for various modal dialog needs within an application.
 *
 * @template Result The expected result type that the modal might produce, typically used when the modal closes.
 */
class BasicModalComponent {
    constructor() {
        this.injector = inject(Injector);
        /**
         * The reference to the modal.
         *
         * @type {ModalRef<BasicModalOptions<Result>>}
         */
        this.modalRef = injectModalRef();
    }
    get data() {
        return this.modalRef.data;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BasicModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: BasicModalComponent, isStandalone: true, selector: "odx-basic-modal", ngImport: i0, template: "@if (data.heroIcon; as icon) {\n  <odx-modal-hero [icon]=\"icon\" [variant]=\"data.heroVariant\">\n    <ng-template [odxDynamicView]=\"data.title\" />\n  </odx-modal-hero>\n} @else {\n  <odx-modal-header>\n    <odx-area-header>\n      <ng-template [odxDynamicView]=\"data.title\" />\n    </odx-area-header>\n  </odx-modal-header>\n}\n\n<odx-modal-content>\n  <ng-template [odxDynamicView]=\"data.content\" [odxDynamicViewInjector]=\"injector\" />\n</odx-modal-content>\n\n<odx-modal-footer>\n  @for (action of data.actions; track $index) {\n    <button odxButton [variant]=\"action.variant\" [odxModalClose]=\"action.data\">\n      {{ action.label }}\n    </button>\n  }\n</odx-modal-footer>\n", dependencies: [{ kind: "ngmodule", type: CoreModule }, { kind: "directive", type: DynamicViewDirective, selector: "ng-template[odxDynamicView]", inputs: ["odxDynamicView", "odxDynamicViewInjector", "odxDynamicViewContext"] }, { kind: "component", type: ButtonComponent, selector: "button[odxButton], a[odxButton]", inputs: ["variant", "size"] }, { kind: "component", type: AreaHeaderComponent, selector: "odx-area-header", inputs: ["size"] }, { kind: "component", type: ModalHeaderComponent, selector: "odx-modal-header" }, { kind: "component", type: ModalHeroComponent, selector: "odx-modal-hero", inputs: ["icon", "iconSet", "variant"] }, { kind: "component", type: ModalFooterComponent, selector: "odx-modal-footer" }, { kind: "directive", type: ModalContentDirective, selector: "odx-modal-content" }, { kind: "directive", type: ModalCloseDirective, selector: "[odxModalClose]", inputs: ["odxModalClose"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BasicModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-basic-modal', standalone: true, imports: [
                        CoreModule,
                        DynamicViewDirective,
                        ButtonComponent,
                        AreaHeaderComponent,
                        ModalHeaderComponent,
                        ModalHeroComponent,
                        ModalFooterComponent,
                        ModalContentDirective,
                        ModalCloseDirective,
                    ], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "@if (data.heroIcon; as icon) {\n  <odx-modal-hero [icon]=\"icon\" [variant]=\"data.heroVariant\">\n    <ng-template [odxDynamicView]=\"data.title\" />\n  </odx-modal-hero>\n} @else {\n  <odx-modal-header>\n    <odx-area-header>\n      <ng-template [odxDynamicView]=\"data.title\" />\n    </odx-area-header>\n  </odx-modal-header>\n}\n\n<odx-modal-content>\n  <ng-template [odxDynamicView]=\"data.content\" [odxDynamicViewInjector]=\"injector\" />\n</odx-modal-content>\n\n<odx-modal-footer>\n  @for (action of data.actions; track $index) {\n    <button odxButton [variant]=\"action.variant\" [odxModalClose]=\"action.data\">\n      {{ action.label }}\n    </button>\n  }\n</odx-modal-footer>\n" }]
        }] });

/**
 * A component that represents a modal dialog with dynamic content and customizable animations.
 * It supports both modal and sidesheet variants with configurable animations for each type.
 */
let ModalComponent = class ModalComponent {
    constructor() {
        this.modalRef = injectModalRef();
        this.injector = inject(Injector);
        this.context = { $implicit: this.modalRef };
        this.element = injectElement();
        /**
         * Size of the modal as a CSS modifier based on modal options.
         *
         * @type {ModalSize}
         */
        this.size = this.modalRef.options.size;
        /**
         * Variant of the modal as a CSS modifier that influences animation choices.
         *
         * @type {ModalVariant}
         */
        this.variant = this.modalRef.options.variant;
        /**
         * Boolean that indicates if the content is a component type rather than a template.
         *
         * @type {boolean}
         * @default false
         */
        this.withComponent = false;
        detectControllerChanges(this.modalRef).subscribe(() => {
            this.size = this.modalRef.options.size;
        });
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, 'content', false)) {
            this.withComponent = !isTemplateRef(this.content);
        }
    }
    preventNativeDismiss(event) {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.modalRef.options.dismissable && this.modalRef.destroy();
        }
    }
    handleDialogClose() {
        this.modalRef.dismiss();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: ModalComponent, isStandalone: true, selector: "dialog[odx-dialog-modal]", inputs: { content: "content" }, host: { listeners: { "keydown": "preventNativeDismiss($event)", "cancel": "handleDialogClose()", "close": "handleDialogClose()" }, properties: { "attr.id": "modalRef.options.id", "attr.aria-labelledby": "modalRef.modalTitleId", "attr.role": "\"dialog\"" } }, usesOnChanges: true, ngImport: i0, template: "<div\n  [odxClickOutsideActive]=\"modalRef.isActive() && modalRef.options.dismissable && modalRef.options.interactiveBackdrop\"\n  (odxClickOutside)=\"modalRef.dismiss()\"\n  class=\"odx-modal__container\"\n>\n  <ng-template [odxDynamicView]=\"content\" [odxDynamicViewContext]=\"withComponent ? {} : context\" [odxDynamicViewInjector]=\"injector\" />\n</div>\n", dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i1.ClickOutsideDirective, selector: "[odxClickOutside]", inputs: ["odxClickOutsideActive"], outputs: ["odxClickOutside"] }, { kind: "ngmodule", type: CoreModule }, { kind: "directive", type: DynamicViewDirective, selector: "ng-template[odxDynamicView]", inputs: ["odxDynamicView", "odxDynamicViewInjector", "odxDynamicViewContext"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], ModalComponent.prototype, "size", void 0);
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], ModalComponent.prototype, "variant", void 0);
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], ModalComponent.prototype, "withComponent", void 0);
ModalComponent = __decorate([
    CSSComponent('modal'),
    __metadata("design:paramtypes", [])
], ModalComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'dialog[odx-dialog-modal]', imports: [A11yModule, ActionGroupComponent, ButtonComponent, IconComponent, CoreModule, DynamicViewDirective], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                        '[attr.id]': 'modalRef.options.id',
                        '[attr.aria-labelledby]': 'modalRef.modalTitleId',
                        '[attr.role]': '"dialog"',
                    }, template: "<div\n  [odxClickOutsideActive]=\"modalRef.isActive() && modalRef.options.dismissable && modalRef.options.interactiveBackdrop\"\n  (odxClickOutside)=\"modalRef.dismiss()\"\n  class=\"odx-modal__container\"\n>\n  <ng-template [odxDynamicView]=\"content\" [odxDynamicViewContext]=\"withComponent ? {} : context\" [odxDynamicViewInjector]=\"injector\" />\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { size: [], variant: [], withComponent: [], content: [{
                type: Input
            }], preventNativeDismiss: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], handleDialogClose: [{
                type: HostListener,
                args: ['cancel']
            }, {
                type: HostListener,
                args: ['close']
            }] } });

/**
 * Utilizes `createConfigTokens` to create configuration tokens and utility functions for managing modal configurations.
 * This setup enables easy injection and provision of default global settings for modals throughout the application,
 * fostering consistency and ease of customization.
 *
 * The generated tokens and functions include:
 * - `ModalDefaultConfig`: The default configuration values for modals.
 * - `ModalConfig`: An Angular `InjectionToken` for injecting modal configuration into components or services.
 * - `injectModalConfig`: A utility function for retrieving the current modal configuration, considering any overrides.
 * - `provideModalConfig`: A function that allows specifying overrides to the default modal configuration.
 *
 * @example
 * ```ts
 * // Example of providing a custom modal configuration in an Angular module
 * @NgModule({
 *   providers: [
 *     provideModalConfig({
 *       size: ModalSize.LARGE,
 *       dismissable: false
 *     })
 *   ]
 * })
 * class AppModule {}
 *
 * // Example of injecting modal configuration in a component
 * @Component({...})
 * export class MyComponent {
 *   constructor(@Inject(ModalConfig) private modalConfig: ModalConfig) {
 *     console.log(this.modalConfig.size); // Outputs the size from the provided or default configuration
 *   }
 * }
 * ```
 */
const { ModalDefaultConfig, ModalConfig, injectModalConfig, provideModalConfig } = createConfigTokens('Modal', '@odx/angular/components/modal', {
    dismissable: true,
    size: ModalSize.MEDIUM,
    dismissOnNavigation: true,
    interactiveBackdrop: true,
    variant: ModalVariant.DEFAULT,
});

/**
 * Service for managing modal dialogs within the application. It supports creating, opening,
 * and dismissing modals dynamically, with comprehensive options for customization and lifecycle management.
 * Utilizes Angular's dynamic view management to instantiate and render modals as needed.
 *
 * @example
 * ```ts
 * // Example of using ModalService to open a modal
 * constructor(private modalService: ModalService) {}
 *
 * openModal() {
 *   const modalRef = this.modalService.open(MyModalContentComponent, {
 *     data: { someData: 'test' },
 *     size: ModalSize.SMALL,
 *   });
 *
 *   modalRef.onClose$.subscribe(result => {
 *     console.log('Modal closed with result:', result);
 *   });
 * }
 * ```
 */
class ModalService {
    constructor(location) {
        this.config = injectModalConfig();
        this.openModals = new Queue();
        this.injector = inject(Injector);
        this.dynamicViewService = inject(DynamicViewService);
        /**
         * An observable that emits the current value of the openModals subject.
         *
         * @emits {ModalRef[]} openModals$ - The current array of open modals.
         */
        this.openModals$ = this.openModals.value$;
        location.onUrlChange(() => {
            for (const modalRef of this.openModals.toArray()) {
                if (modalRef.options.dismissOnNavigation) {
                    modalRef.destroy();
                }
            }
        });
    }
    /**
     * Dismisses all open modals.
     */
    dismissAll() {
        for (const modalRef of this.openModals.toArray()) {
            modalRef.dismiss();
        }
    }
    /**
     * Retrieves the modal reference by its ID.
     *
     * @template Data The type of data passed to the modal.
     * @template Result The type of result returned by the modal.
     * @param {ModalOptions['id']} id The ID of the modal.
     * @returns {ModalRef<Data, Result> | null} - The modal reference if found, otherwise null.
     */
    getModalById(id) {
        return this.openModals.get(id);
    }
    create(modal, options) {
        return this.open(BasicModalComponent, {
            data: modal,
            size: ModalSize.SMALL,
            ...options,
        });
    }
    open(content, options) {
        const mergedOptions = deepmerge(this.config, { id: getUniqueId('odx-modal') }, options ?? {});
        if (this.getModalById(mergedOptions.id)) {
            throw Error(`Modal with ID ${mergedOptions.id} already open`);
        }
        const modalRef = new ModalRef(mergedOptions, (ref) => this.openModals.isFirst(ref));
        this.attachModal(modalRef, content);
        this.openModals.add(modalRef);
        return modalRef;
    }
    attachModal(modalRef, content) {
        const viewRef = this.dynamicViewService.createView(ModalComponent, {
            context: { content },
            injector: Injector.create({
                providers: [provideModalRef(modalRef)],
                parent: this.injector,
            }),
            host: modalRef.options.host,
        });
        const dialog = viewRef.getElement();
        const onDestroy = async () => {
            dialog?.classList.add('is-hidden');
            await waitForAnimations(dialog);
            dialog?.close();
            this.openModals.remove(modalRef);
            viewRef.destroy();
        };
        modalRef.onDestroy$.pipe(finalize(onDestroy)).subscribe();
        deferFn(() => {
            dialog?.isConnected && dialog?.showModal();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalService, deps: [{ token: i1$1.Location }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1$1.Location }] });

/**
 * A directive that simplifies the creation and management of modals directly from Angular templates.
 * It allows for declarative modal instantiation with full control over modal options, and supports
 * capturing modal close and dismiss events.
 *
 * Usage:
 * Apply `odxModal` to an `<ng-template>` element and bind it to modal options.
 */
class ModalDirective {
    constructor() {
        this.modalService = inject(ModalService);
        this.modalRef = null;
        this.template = inject(TemplateRef);
        /**
         * Event emitter for closing the modal.
         *
         * @emits {any} The result of the modal close event.
         */
        // eslint-disable-next-line @angular-eslint/no-output-rename
        this.modalClose = new EventEmitter();
        /**
         * Event emitter for dismissing the modal.
         *
         * @emits {any} The result of the modal dismiss event.
         */
        // eslint-disable-next-line @angular-eslint/no-output-rename
        this.modalDismiss = new EventEmitter();
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, 'modalOptions') && this.modalOptions) {
            this.modalRef?.update(this.modalOptions);
        }
    }
    ngOnDestroy() {
        this.dismiss();
    }
    /**
     * Opens the modal based on the provided template and options. Binds modal result and dismissal outputs to EventEmitter properties.
     */
    open() {
        this.modalRef = this.modalService.open(this.template, this.modalOptions || {});
        this.modalRef.onClose$.subscribe((result) => this.modalClose.next(result));
        this.modalRef.onDismiss$.subscribe((result) => this.modalDismiss.next(result));
    }
    /**
     * Dismisses the currently opened modal.
     */
    dismiss() {
        this.modalRef?.dismiss();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: ModalDirective, isStandalone: true, selector: "ng-template[odxModal]", inputs: { modalOptions: ["odxModal", "modalOptions"] }, outputs: { modalClose: "odxModalClose", modalDismiss: "odxModalDismiss" }, exportAs: ["odxModal"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: 'ng-template[odxModal]',
                    exportAs: 'odxModal',
                }]
        }], propDecorators: { modalOptions: [{
                type: Input,
                args: ['odxModal']
            }], modalClose: [{
                type: Output,
                args: ['odxModalClose']
            }], modalDismiss: [{
                type: Output,
                args: ['odxModalDismiss']
            }] } });

const modules = [
    ModalComponent,
    ModalHeaderComponent,
    ModalHeroComponent,
    ModalFooterComponent,
    ModalDirective,
    ModalCloseDirective,
    ModalContentDirective,
    ModalDismissDirective,
    PreventMethodDialogDirective,
    PreventFormMethodDialogDirective,
];
class ModalModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: ModalModule, imports: [ButtonComponent, ButtonGroupComponent, ModalComponent,
            ModalHeaderComponent,
            ModalHeroComponent,
            ModalFooterComponent,
            ModalDirective,
            ModalCloseDirective,
            ModalContentDirective,
            ModalDismissDirective,
            PreventMethodDialogDirective,
            PreventFormMethodDialogDirective], exports: [CoreModule, AreaHeaderModule, ButtonComponent, ButtonGroupComponent, ModalComponent,
            ModalHeaderComponent,
            ModalHeroComponent,
            ModalFooterComponent,
            ModalDirective,
            ModalCloseDirective,
            ModalContentDirective,
            ModalDismissDirective,
            PreventMethodDialogDirective,
            PreventFormMethodDialogDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalModule, imports: [ButtonComponent, ButtonGroupComponent, ModalComponent,
            ModalHeaderComponent,
            ModalHeroComponent, CoreModule, AreaHeaderModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ButtonComponent, ButtonGroupComponent, ...modules],
                    exports: [CoreModule, AreaHeaderModule, ButtonComponent, ButtonGroupComponent, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BasicModalComponent, ModalCloseDirective, ModalComponent, ModalConfig, ModalContentDirective, ModalDefaultConfig, ModalDirective, ModalDismissDirective, ModalFooterComponent, ModalHeaderComponent, ModalHeroComponent, ModalHeroVariant, ModalModule, ModalRef, ModalService, ModalSize, ModalVariant, PreventFormMethodDialogDirective, PreventMethodDialogDirective, injectModalConfig, injectModalRef, provideModalConfig, provideModalRef };
